import { isNil, round, sum } from "lodash-es";
import { useEffect, useMemo } from "react";

import {
  calculateStudentOutcomeScore,
  filterAssessmentsForStudent,
  getAssessmentStatusLabel,
  getPageSpecificAssessmentData,
} from "./utils";

import { AssessmentStatus } from "api/api-data-types";
import { ASSESSMENT_VIEW_QUERY_PARAM } from "components/constants";
import { getPageType, getPageTypeShort, tableDateFormat } from "components/materials/page/helpers";
import { displayOutcomeName } from "components/outcomes/helpers";
import {
  ActivityType,
  PageGroupUserType,
  PageType,
  RubricType,
  SectionScoreDisplayFormat,
} from "components/server-types";
import { t } from "i18n/i18n";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  PageWithAssessmentsType,
  activitySelectors,
  pageGroupUserSelectors,
  pageOutcomeSelectors,
  selectCurrentOrg,
  selectCurrentSection,
  selectCurrentSectionId,
  selectFullSectionOutcomes,
  selectPagesWithAssessments,
  selectUsersInCurrentSection,
  submissionSelectors,
} from "store/selectors";
import { selectAllAssessmentInfo } from "store/selectors/computed";
import { NestedSectionOutcomeType, UserWithSectionUserType } from "store/selectors/types";
import { averageAndRound } from "utils/math";
import { useQueryParamsManager } from "utils/routing";
import { getUserFullName } from "utils/user-utils";

export const useIsAssessing = () => {
  const { getQueryParam } = useQueryParamsManager();
  const { canAuthorSection } = useCapabilities();

  if (!canAuthorSection) {
    return false;
  }
  // Instructors can assess, so check the query param
  // to see if they are currently assessing
  const isAssessing = getQueryParam(ASSESSMENT_VIEW_QUERY_PARAM);
  return !!isAssessing;
};

const filterPagesByType = (
  type: "assignment" | "prep_work" | "activities" | "other",
  publishedPagesWithAssessments: PageWithAssessmentsType[],
  activityMap: Record<string, ActivityType>,
) => publishedPagesWithAssessments.filter((page) => getPageTypeShort(page, activityMap) === type);

const getAssessmentPoints = (
  pageMap: { [id: string]: PageWithAssessmentsType },
  student: UserWithSectionUserType,
  pageGroupUsers: PageGroupUserType[],
): { assessedPoints: "-" | number; maxPoints: number } => {
  const studentPageAssessments = Object.values(pageMap).map((page) =>
    filterAssessmentsForStudent(page.assessments, student, pageGroupUsers),
  );

  if (studentPageAssessments.every((assessments) => assessments.length === 0))
    return { assessedPoints: "-", maxPoints: 0 };

  const filteredStudentPageAssessments = studentPageAssessments.filter(
    (assessments) => assessments.length > 0,
  );

  const assessedPoints = filteredStudentPageAssessments.reduce((totalPoints, assessments) => {
    const points = assessments
      .filter((a) => a.points !== null && a.points !== undefined)
      .map((a) => a.points);
    return totalPoints + (points.length ? averageAndRound(points) : 0);
  }, 0);

  const maxPoints = filteredStudentPageAssessments.reduce((totalPoints, assessments) => {
    if (assessments.every((a) => isNil(a.points))) return totalPoints;
    return totalPoints + pageMap[assessments[0].submission.page_id].max_points;
  }, 0);

  return { assessedPoints, maxPoints };
};

const calculateWeightedPercent = (
  studentPoints: number | "-",
  totalPoints: number,
  maxValue: number,
  precision = 1,
) => {
  if (studentPoints === "-" || !maxValue || totalPoints === 0) return `-`;
  return round((studentPoints / totalPoints) * (maxValue / 100) * 100, precision);
};

export const calculateRubricScore = (orgRubric: RubricType | undefined, percent: number) => {
  if (!orgRubric) return "-";

  const baseLevel = orgRubric.findLast((level) => percent >= level.conversion_percent);
  const indexOfBaseLevel = orgRubric.findIndex(
    (level) =>
      level.score === baseLevel?.score &&
      level.conversion_percent === baseLevel?.conversion_percent,
  );

  if (!baseLevel || indexOfBaseLevel === -1) return "-";

  if (indexOfBaseLevel === orgRubric.length - 1) {
    return baseLevel.score;
  }

  const nextLevel = orgRubric[indexOfBaseLevel + 1];
  const percentDiff = nextLevel.conversion_percent - baseLevel.conversion_percent;
  const scoreDiff = nextLevel.score - baseLevel.score;
  const slope = scoreDiff / percentDiff;
  return round(baseLevel.score + slope * (percent - baseLevel.conversion_percent), 1);
};

const calculateTotalSectionScore = (
  prepWorkSectionScore: number | "-",
  activitiesSectionScore: number | "-",
  assignmentsSectionScore: number | "-",
  prepWorkMaxScore: number,
  activitiesMaxScore: number,
  assignmentsMaxScore: number,
  sectionScoreDisplayFormat: SectionScoreDisplayFormat,
  orgRubric: RubricType,
) => {
  if (
    (prepWorkSectionScore === "-" && !!prepWorkMaxScore) ||
    (activitiesSectionScore === "-" && !!activitiesMaxScore) ||
    (assignmentsSectionScore === "-" && !!assignmentsMaxScore)
  ) {
    return {
      totalSectionScorePercentDisplay: "-",
      totalSectionScoreForDisplay: "-",
      totalSectionScoreForDisplayIncludingMaxValue: "-",
    };
  }

  const sectionScores = [
    prepWorkMaxScore ? prepWorkSectionScore : 0,
    activitiesMaxScore ? activitiesSectionScore : 0,
    assignmentsMaxScore ? assignmentsSectionScore : 0,
  ];

  const sectionScore = round(sum(sectionScores.filter((score) => score !== "-")), 1);

  const sectionScoreStr = `${sectionScore}%`;
  let totalSectionScoreForDisplay = sectionScoreStr;
  let totalSectionScoreForDisplayIncludingMaxValue = sectionScoreStr;

  if (
    sectionScoreDisplayFormat === SectionScoreDisplayFormat.Scale_4 ||
    sectionScoreDisplayFormat === SectionScoreDisplayFormat.Scale_5
  ) {
    totalSectionScoreForDisplay = `${calculateRubricScore(orgRubric, sectionScore)}`;
    totalSectionScoreForDisplayIncludingMaxValue = `${totalSectionScoreForDisplay} / ${sectionScoreDisplayFormat === SectionScoreDisplayFormat.Scale_4 ? 4 : 5}`;
  }

  return {
    totalSectionScorePercentDisplay: sectionScoreStr,
    totalSectionScoreForDisplay,
    totalSectionScoreForDisplayIncludingMaxValue,
  };
};

export type AssessmentDashboardDataType = {
  allAssessmentsCSVData: {
    headers: string[];
    rows: (string | number)[][];
  };
  allAssessmentsTableRows: Array<{
    page: PageType;
    id: string;
    title: string;
    topicTitle: string | null;
    status: AssessmentStatus;
    unassessedLateCount: number;
    assessedSubmissionsCount: number;
    isAssessmentComplete: boolean;
    isMarkedComplete: boolean;
    publishing: string;
    assessedCount: number;
    pageType: string;
    avgPoints: string;
    outcomesCount: number;
    outcomesTooltip: string;
    lastSubmittedDate: string;
  }>;
  studentPointsPerPageCSVData: {
    headers: string[];
    rows: (string | number)[][];
  };
  studentPointsPerPageTableRows: Array<{
    page: PageType;
    id: string;
    title: string;
    pageType: string;
    avgPoints: string;
    studentScores: (string | number)[];
  }>;
  studentScoresPerOutcomeCSVData: {
    headers: string[];
    rows: (string | number | null)[][];
  };
  studentScoresPerOutcomeTableRows: Array<{
    student: UserWithSectionUserType;
    studentName: string;
    outcomeScores: (number | null)[];
  }>;
  studentSectionScoresTableRows: Array<{
    studentName: string;
    id: string;
    activitiesMaxValue: number;
    activitiesPercentScore: string;
    assignmentsMaxValue: number;
    assignmentsPercentScore: string;
    prepWorkMaxValue: number;
    prepWorkPercentScore: string;
    /** The section score in a percentage display should always be returned. Always included in reports. */
    totalSectionScorePercentDisplay: string;
    /**
     * The section score in the configured display format (percentage or 0-X scale).
     * Included in reports if the org is configured to be displayed on the 0-X scale. */
    totalSectionScoreForDisplay: string;
    /**
     * The section score in the configured display format including the max value
     * (e.g., X / 4 for 0-4 scale, X / 5 for 0-5 scale, X% for percent). This is
     * shown to the users on the student section scores table. */
    totalSectionScoreForDisplayIncludingMaxValue: string;
  }>;
  studentSectionScoresCSVData: {
    headers: string[];
    rows: string[][];
  };
  sectionStudents: UserWithSectionUserType[];
  sectionOutcomes: NestedSectionOutcomeType[];
  publishedPagesWithAssessments: PageType[];
};

export const useAssessmentDashboardData = () => {
  const currentSection = useAppSelector(selectCurrentSection);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const sectionUsers = useAppSelector(selectUsersInCurrentSection);
  const sectionOutcomes = useAppSelector(selectFullSectionOutcomes);
  const pageOutcomes = useAppSelector(pageOutcomeSelectors.selectAll);
  const pageGroupUsers = useAppSelector(pageGroupUserSelectors.selectAll);
  const allSubmissions = useAppSelector(submissionSelectors.selectAll);
  const pagesWithAssessments = useAppSelector(selectPagesWithAssessments);
  const allAssessmentInfo = useAppSelector(selectAllAssessmentInfo);
  const currentOrg = useAppSelector(selectCurrentOrg);
  const activityMap = useAppSelector(activitySelectors.selectEntities);

  const sectionScoreDisplayFormat =
    currentOrg?.section_score_display_format || SectionScoreDisplayFormat.Percent;

  const orgRubric = currentOrg?.rubric;

  useEffect(() => {
    if (sectionId) {
      storeApi.section_users.list({ section_id: sectionId });
      storeApi.section_outcomes.list({ section_id: sectionId });
      storeApi.page_outcomes.list({ section_id: sectionId });
      storeApi.page_groups.list({ section_id: sectionId });
      storeApi.page_group_users.list({ section_id: sectionId });
      storeApi.submissions.list({ section_id: sectionId });
      storeApi.topics.list({ section_id: sectionId });
      storeApi.activities.list({ section_id: sectionId });
    }
  }, [sectionId]);

  const sectionStudents = sectionUsers
    .filter((u) => u.role === "student")
    .sort((a, b) => getUserFullName(a).localeCompare(getUserFullName(b)));

  const pagesIdsWithSubmissions = useMemo(
    () => new Set(allSubmissions.map((submission) => submission.page_id)),
    [allSubmissions],
  );

  const allAssessments = pagesWithAssessments.map((p) => p.assessments).flat();
  // Here we specifically only want to show finalized assessments on pages with points
  const publishedPagesWithAssessments = pagesWithAssessments.filter(
    (page) => page.assessments_published_at && page.max_points,
  );
  const allScoredSubmissions = allAssessments.map((a) => a.submission);

  // Filter pages that have submissions and either max points or outcomes
  const filteredPagesWithAssessments = useMemo(
    () =>
      pagesWithAssessments.filter(
        (page) =>
          (page.max_points || pageOutcomes.some((po) => po.page_id === page.id)) &&
          pagesIdsWithSubmissions.has(page.id),
      ),
    [pagesWithAssessments, pageOutcomes, pagesIdsWithSubmissions],
  );

  const allAssessmentsCSVData = useMemo(() => {
    const headers = [
      t("assessment_dashboard.all.page_topic_header_title"),
      t("assessment_dashboard.all.page_header_title"),
      t("assessment_dashboard.all.status_header_title"),
      t("assessment_dashboard.all.publishing_header_title"),
      t("assessment_dashboard.all.assessed_header_title"),
      t("assessment_dashboard.all.page_type_header_title"),
      t("assessment_dashboard.all.avg_points_header_title"),
      t("assessment_dashboard.all.outcomes_header_title"),
      t("assessment_dashboard.all.last_submitted_header_title"),
    ];

    const rows = filteredPagesWithAssessments.map((page) => {
      const { pointList, latestSubmissionDate, pageSpecificOutcomes } =
        getPageSpecificAssessmentData(page, pageOutcomes, tableDateFormat);
      const assessmentStatus = allAssessmentInfo[page.id]?.status;
      const assessedSubmissionsCount = allAssessmentInfo[page.id]?.assessed_submissions_count || 0;
      const isMarkedComplete = Boolean(page.assessing_marked_complete_at);
      const isAssessmentComplete = assessmentStatus === AssessmentStatus.ASSESSED;

      return [
        page.topic?.title || "-",
        page.title,
        !assessmentStatus || assessmentStatus === AssessmentStatus.NOT_ASSESSABLE
          ? "-"
          : getAssessmentStatusLabel(
              isMarkedComplete,
              assessedSubmissionsCount,
              isAssessmentComplete,
            ),
        page.assessments_published_at ? "Published" : "Draft",
        page.assessments.length,
        getPageType(page, activityMap),
        pointList.length ? `${averageAndRound(pointList)}/${page.max_points}` : "-",
        pageSpecificOutcomes.length,
        latestSubmissionDate || "-",
      ];
    });

    return { headers, rows };
  }, [filteredPagesWithAssessments, pageOutcomes, activityMap, allAssessmentInfo]);

  const allAssessmentsTableRows = useMemo(
    () =>
      filteredPagesWithAssessments.map((page) => {
        const { pointList, latestSubmissionDate, pageSpecificOutcomes } =
          getPageSpecificAssessmentData(page, pageOutcomes, tableDateFormat);
        const pageSpecificOutcomeDisplayNames = pageSpecificOutcomes.flatMap((po) => {
          const sectionOutcome = sectionOutcomes.find((co) => co.id === po.section_outcome_id);
          return sectionOutcome ? [displayOutcomeName(sectionOutcome.outcome)] : [];
        });
        const outcomesTooltip = pageSpecificOutcomeDisplayNames.join(", ");

        return {
          page,
          id: page.id,
          title: page.title,
          topicTitle: page.topic?.title,
          status: allAssessmentInfo[page.id]?.status,
          unassessedLateCount: allAssessmentInfo[page.id]?.unassessed_late_count || 0,
          isMarkedComplete: Boolean(page.assessing_marked_complete_at),
          isAssessmentComplete: allAssessmentInfo[page.id]?.status === AssessmentStatus.ASSESSED,
          assessedSubmissionsCount: allAssessmentInfo[page.id]?.assessed_submissions_count || 0,
          publishing: page.assessments_published_at ? "Published" : "Draft",
          assessedCount: page.assessments.length,
          pageType: getPageType(page, activityMap),
          avgPoints: pointList.length ? `${averageAndRound(pointList)}/${page.max_points}` : "-",
          outcomesCount: pageSpecificOutcomes.length,
          outcomesTooltip,
          lastSubmittedDate: latestSubmissionDate || "-",
        };
      }),
    [filteredPagesWithAssessments, pageOutcomes, activityMap, sectionOutcomes, allAssessmentInfo],
  );

  const studentPointsPerPageTableRows = useMemo(
    () =>
      publishedPagesWithAssessments.map((page) => {
        const { pointList } = getPageSpecificAssessmentData(page, pageOutcomes, tableDateFormat);
        const studentScores = sectionStudents.map((student) => {
          const assessmentsForUser = filterAssessmentsForStudent(
            page.assessments,
            student,
            pageGroupUsers,
          );
          // Technically, this should always only be a single score,
          // but we average it (mean value) just in case
          const pointsForUser = assessmentsForUser
            .filter((a) => a.points !== null && a.points !== undefined)
            .map((a) => a.points);
          return pointsForUser.length ? averageAndRound(pointsForUser) : "-";
        });
        return {
          page,
          id: page.id,
          title: page.title,
          pageType: getPageType(page, activityMap),
          avgPoints: pointList.length ? `${averageAndRound(pointList)}/${page.max_points}` : "-",
          studentScores,
        };
      }),
    [publishedPagesWithAssessments, pageOutcomes, activityMap, sectionStudents, pageGroupUsers],
  );

  const studentPointsPerPageCSVData = useMemo(() => {
    const headers = [
      t("assessment_dashboard.all.page_header_title"),
      t("assessment_dashboard.all.page_type_header_title"),
      t("assessment_dashboard.all.avg_points_header_title"),
      ...sectionStudents.map((student) => getUserFullName(student)),
    ];

    const rows = publishedPagesWithAssessments.map((page) => {
      const { pointList } = getPageSpecificAssessmentData(page, pageOutcomes, tableDateFormat);
      const studentScores = sectionStudents.map((student) => {
        const assessmentsForUser = filterAssessmentsForStudent(
          page.assessments,
          student,
          pageGroupUsers,
        );
        const pointsForUser = assessmentsForUser
          .filter((a) => a.points !== null && a.points !== undefined)
          .map((a) => a.points);
        return pointsForUser.length ? averageAndRound(pointsForUser) : "-";
      });

      return [
        page.title,
        getPageType(page, activityMap),
        pointList.length ? `${averageAndRound(pointList)}/${page.max_points}` : "-",
        ...studentScores,
      ];
    });

    return { headers, rows };
  }, [publishedPagesWithAssessments, sectionStudents, pageGroupUsers, activityMap, pageOutcomes]);

  // TODO: Only show outcome scores for finalized assessments
  const studentScoresPerOutcomeTableRows = useMemo(
    () =>
      sectionStudents.map((student) => {
        const userGroupIds = pageGroupUsers
          .filter((pgu) => pgu.section_user_id === student.section_user_id)
          .map((pgu) => pgu.page_group_id);

        const userSubmissions = allScoredSubmissions.filter(
          (submission) =>
            submission.section_user_id === student.section_user_id ||
            (submission.page_group_id && userGroupIds.includes(submission.page_group_id)),
        );

        const userAssessments = allAssessments.filter((a) =>
          userSubmissions.find((submission) => submission.id === a.submission_id),
        );

        const outcomeScores = sectionOutcomes.map((sectionOutcome) => {
          return calculateStudentOutcomeScore(userAssessments, sectionOutcome.id, pageOutcomes);
        });

        return {
          student,
          studentName: getUserFullName(student),
          outcomeScores,
        };
      }),
    [
      sectionStudents,
      sectionOutcomes,
      pageGroupUsers,
      allScoredSubmissions,
      allAssessments,
      pageOutcomes,
    ],
  );

  const studentScoresPerOutcomeCSVData = useMemo(() => {
    const headers = [
      t("assessment_dashboard.all.student_name_header_title"),
      ...sectionOutcomes.map((sectionOutcome) => displayOutcomeName(sectionOutcome.outcome)),
    ];

    const rows = sectionStudents.map((student) => {
      const userGroupIds = pageGroupUsers
        .filter((pgu) => pgu.section_user_id === student.section_user_id)
        .map((pgu) => pgu.page_group_id);

      const userSubmissions = allScoredSubmissions.filter(
        (submission) =>
          submission.section_user_id === student.section_user_id ||
          (submission.page_group_id && userGroupIds.includes(submission.page_group_id)),
      );

      const userAssessments = allAssessments.filter((assessment) =>
        userSubmissions.find((submission) => submission.id === assessment.submission_id),
      );

      const outcomeScores = sectionOutcomes.map((sectionOutcome) => {
        return calculateStudentOutcomeScore(userAssessments, sectionOutcome.id, pageOutcomes);
      });

      return [getUserFullName(student), ...outcomeScores];
    });

    return { headers, rows };
  }, [
    sectionStudents,
    sectionOutcomes,
    pageGroupUsers,
    allScoredSubmissions,
    allAssessments,
    pageOutcomes,
  ]);

  const studentSectionScoresTableRows = useMemo(() => {
    const sectionScoreEnabled = currentSection?.section_score_enabled;
    if (!sectionScoreEnabled) return [];

    const activitiesMaxValue = currentSection?.section_score_activities_percentage ?? 0;
    const prepWorkMaxValue = currentSection?.section_score_prepwork_percentage ?? 0;
    const assignmentsMaxValue = currentSection?.section_score_assignments_percentage ?? 0;

    const activityPageMap = filterPagesByType(
      "activities",
      publishedPagesWithAssessments,
      activityMap,
    ).reduce(
      (map, activityPage) => ({
        ...map,
        [activityPage.id]: activityPage,
      }),
      {},
    );

    const prepWorkPageMap = filterPagesByType(
      "prep_work",
      publishedPagesWithAssessments,
      activityMap,
    ).reduce(
      (map, activityPage) => ({
        ...map,
        [activityPage.id]: activityPage,
      }),
      {},
    );

    const assignmentPageMap = filterPagesByType(
      "assignment",
      publishedPagesWithAssessments,
      activityMap,
    ).reduce(
      (map, activityPage) => ({
        ...map,
        [activityPage.id]: activityPage,
      }),
      {},
    );

    return sectionStudents.map((student) => {
      const {
        assessedPoints: studentPointsFromActivityPages,
        maxPoints: totalPointsFromActivityPages,
      } = getAssessmentPoints(activityPageMap, student, pageGroupUsers);

      const {
        assessedPoints: studentPointsFromPrepWorkPages,
        maxPoints: totalPointsFromPrepWorkPages,
      } = getAssessmentPoints(prepWorkPageMap, student, pageGroupUsers);

      const {
        assessedPoints: studentPointsFromAssignmentPages,
        maxPoints: totalPointsFromAssignmentPages,
      } = getAssessmentPoints(assignmentPageMap, student, pageGroupUsers);

      const activitiesPercentScore = calculateWeightedPercent(
        studentPointsFromActivityPages,
        totalPointsFromActivityPages,
        activitiesMaxValue,
      );
      const prepWorkPercentScore = calculateWeightedPercent(
        studentPointsFromPrepWorkPages,
        totalPointsFromPrepWorkPages,
        prepWorkMaxValue,
      );
      const assignmentsPercentScore = calculateWeightedPercent(
        studentPointsFromAssignmentPages,
        totalPointsFromAssignmentPages,
        assignmentsMaxValue,
      );

      const {
        totalSectionScorePercentDisplay,
        totalSectionScoreForDisplay,
        totalSectionScoreForDisplayIncludingMaxValue,
      } = calculateTotalSectionScore(
        prepWorkPercentScore,
        activitiesPercentScore,
        assignmentsPercentScore,
        prepWorkMaxValue,
        activitiesMaxValue,
        assignmentsMaxValue,
        sectionScoreDisplayFormat,
        orgRubric ?? [],
      );

      return {
        studentName: getUserFullName(student),
        id: student.id,
        activitiesMaxValue,
        activitiesPercentScore: `${activitiesPercentScore} / ${activitiesMaxValue}%`,
        assignmentsMaxValue,
        assignmentsPercentScore: `${assignmentsPercentScore} / ${assignmentsMaxValue}%`,
        prepWorkMaxValue,
        prepWorkPercentScore: `${prepWorkPercentScore} / ${prepWorkMaxValue}%`,
        totalSectionScorePercentDisplay,
        totalSectionScoreForDisplay,
        totalSectionScoreForDisplayIncludingMaxValue,
      };
    });
  }, [
    sectionStudents,
    activityMap,
    currentSection,
    pageGroupUsers,
    publishedPagesWithAssessments,
    sectionScoreDisplayFormat,
    orgRubric,
  ]);

  const studentSectionScoresCSVData = useMemo(() => {
    if (studentSectionScoresTableRows.length === 0) {
      return { headers: [], rows: [] };
    }

    const maxPrepWorkPercent = studentSectionScoresTableRows[0].prepWorkMaxValue;
    const maxActivitiesPercent = studentSectionScoresTableRows[0].activitiesMaxValue;
    const maxAssignmentsPercent = studentSectionScoresTableRows[0].assignmentsMaxValue;

    // Need to escape because 18 / 20% gets reinterpreted as 1 once it's exported to csv.
    const escapeCsvValue = (value: string) => `="${value}"`;

    const headers = [
      t("assessment_dashboard.all.student_name_header_title"),
      maxPrepWorkPercent ? t("assessment_dashboard.grades.prepwork") : null,
      maxActivitiesPercent ? t("assessment_dashboard.grades.activities") : null,
      maxAssignmentsPercent ? t("assessment_dashboard.grades.assignments") : null,
      t("assessment_dashboard.student_section_scores.section_score_percent"),
      sectionScoreDisplayFormat && sectionScoreDisplayFormat !== SectionScoreDisplayFormat.Percent
        ? t(
            `assessment_dashboard.student_section_scores.section_score_${sectionScoreDisplayFormat}`,
          )
        : null,
    ].filter(Boolean);

    const rows = studentSectionScoresTableRows.map((row) =>
      [
        row.studentName,
        maxPrepWorkPercent ? escapeCsvValue(row.prepWorkPercentScore) : null,
        maxActivitiesPercent ? escapeCsvValue(row.activitiesPercentScore) : null,
        maxAssignmentsPercent ? escapeCsvValue(row.assignmentsPercentScore) : null,
        row.totalSectionScorePercentDisplay,
        sectionScoreDisplayFormat && sectionScoreDisplayFormat !== SectionScoreDisplayFormat.Percent
          ? row.totalSectionScoreForDisplay
          : null,
      ].filter(Boolean),
    );

    return { headers, rows };
  }, [studentSectionScoresTableRows, sectionScoreDisplayFormat]);

  return {
    allAssessmentsCSVData,
    allAssessmentsTableRows,
    studentPointsPerPageCSVData,
    studentPointsPerPageTableRows,
    studentScoresPerOutcomeCSVData,
    studentScoresPerOutcomeTableRows,
    studentSectionScoresTableRows,
    studentSectionScoresCSVData,
    sectionStudents,
    sectionOutcomes,
    publishedPagesWithAssessments,
  } as AssessmentDashboardDataType;
};
